// ✅ src/pages/VehicleV2/LubeStockPositionReport.jsx
// Production listing template
// GET /api/lubeStockPositionRpt/init
// GET /api/lubeStockPositionRpt/list

import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import {
  FaSearch,
  FaPrint,
  FaArrowLeft,
  FaFileExcel,
  FaEye,
  FaSyncAlt,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/lubeStockPositionRpt";

const toStr = (v, d = "") => (v === null || v === undefined ? d : String(v)).trim();
const isSelect = (v) => {
  const s = toStr(v);
  return !s || s.toLowerCase() === "select";
};

const ID_COL_RE = /(^id$|_id$|typeid|categoryid|branchid)/i;
const IMPORTANT_COL_RE = /name|item|unit|uom|total|qty|branch|rate|amount|lit|cheyyar|office|karuppur|mipcl|patty|pottaneri|gobian|gestamp|sriper|motherson/i;

const LubeStockPositionReport = () => {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const [showFilters, setShowFilters] = useState(true);
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branchType = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [loadingInit, setLoadingInit] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const isBusy = loadingInit || loadingList;

  const [branches, setBranches] = useState([{ label: "Select", value: "Select" }]);
  const [months, setMonths] = useState([{ label: "Select", value: "Select" }]);
  const [years, setYears] = useState([{ label: "Select", value: "Select" }]);
  const [itemNames, setItemNames] = useState([{ label: "Select", value: "Select" }]);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [locks, setLocks] = useState({ branchLocked: false, branchValue: "" });
  const [itemCategory, setItemCategory] = useState({
    id: "",
    name: "FUELS AND LUBRICANTS",
    locked: true,
  });
  const [filters, setFilters] = useState({
    branch: "Select",
    month: "Select",
    year: "Select",
    itemCategoryId: "",
    itemName: "Select",
  });
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);

  const fieldClass = `w-full max-w-full min-w-0 box-border px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
  }`;
  const labelClass = `block mb-2 text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`;

  const displayColumns = useMemo(() => {
    const all = columns?.length ? columns : rows[0] ? Object.keys(rows[0]) : [];
    if (!all.length) return [];
    const kept = all.filter((c) => IMPORTANT_COL_RE.test(String(c)) && !ID_COL_RE.test(String(c)));
    return kept.length ? kept : all;
  }, [columns, rows]);

  const totalPages = Math.max(1, Math.ceil((rows?.length || 0) / itemsPerPage));
  const pagedRows = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return rows.slice(start, start + itemsPerPage);
  }, [rows, currentPage]);

  const buildPrintQuery = (payload = {}) => {
    const qs = new URLSearchParams();
    Object.entries(payload).forEach(([k, v]) => {
      if (v === undefined || v === null) return;
      const s = String(v).trim();
      if (!s) return;
      qs.set(k, s);
    });
    return qs.toString();
  };

  const fetchInit = async () => {
    setLoadingInit(true);
    try {
      const res = await axios.get(`${API_BASE}/init`, {
        params: { branchType, branchId },
        headers: commonHeaders,
      });
      if (!res.data?.success) throw new Error(res.data?.message || "Init failed");
      const data = res.data;
      setBranches(data.branches || [{ label: "Select", value: "Select" }]);
      setMonths(data.months || [{ label: "Select", value: "Select" }]);
      setYears(data.years || [{ label: "Select", value: "Select" }]);
      setItemNames(data.itemNames || [{ label: "Select", value: "Select" }]);
      setSuppliers(data.suppliers || []);
      setSelectedSuppliers([]);
      setLocks(data.userLock || { branchLocked: false, branchValue: "" });
      setItemCategory({
        id: toStr(data.itemCategory?.id || ""),
        name: toStr(data.itemCategory?.name || "FUELS AND LUBRICANTS"),
        locked: !!data.itemCategory?.locked,
      });
      setFilters((p) => ({
        ...p,
        branch: data.userLock?.branchLocked ? data.userLock?.branchValue || "Select" : p.branch,
        itemCategoryId: toStr(data.itemCategory?.id || p.itemCategoryId),
      }));
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err?.message || "Failed to load dropdowns");
    } finally {
      setLoadingInit(false);
    }
  };

  useEffect(() => {
    fetchInit();
  }, []);

  const fetchList = async () => {
    if (isSelect(filters.branch)) return toast.error("Please select Branch");
    if (isSelect(filters.month)) return toast.error("Please select Month");
    if (isSelect(filters.year)) return toast.error("Please select Year");

    setLoadingList(true);
    try {
      const suppliersCsv = selectedSuppliers.length > 0 ? `${selectedSuppliers.join(",")},` : "";
      const res = await axios.get(`${API_BASE}/list`, {
        params: {
          branchId: filters.branch,
          month: filters.month,
          year: filters.year,
          itemCategoryId: filters.itemCategoryId || itemCategory.id,
          itemId: filters.itemName || "Select",
          suppliersCsv,
          date: "",
        },
        headers: commonHeaders,
      });
      if (!res.data?.success) throw new Error(res.data?.message || "List failed");
      setRows(res.data.rows || []);
      setColumns(res.data.columns || []);
      setCurrentPage(1);
      toast.success(`Found ${(res.data.rows || []).length} records`);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err?.message || "Failed to fetch report");
      setRows([]);
      setColumns([]);
    } finally {
      setLoadingList(false);
    }
  };

  const handleChange = (e) => setFilters({ ...filters, [e.target.name]: e.target.value });
  const handleSearch = () => fetchList();
  const handleViewAll = () => {
    setFilters((p) => ({ ...p, itemName: "Select" }));
    setSelectedSuppliers([]);
    toast.info("Showing records based on selected Month/Year");
    setTimeout(() => fetchList(), 0);
  };
  const handleClearFilters = () => {
    setSelectedSuppliers([]);
    setRows([]);
    setColumns([]);
    setCurrentPage(1);
    setFilters((p) => ({
      ...p,
      branch: locks.branchLocked ? locks.branchValue || "Select" : "Select",
      month: "Select",
      year: "Select",
      itemCategoryId: itemCategory.id || p.itemCategoryId,
      itemName: "Select",
    }));
    toast.info("Filters cleared!");
  };

  const handlePrint = () => {
    if (isSelect(filters.branch)) return toast.error("Please select Branch");
    if (isSelect(filters.month)) return toast.error("Please select Month");
    if (isSelect(filters.year)) return toast.error("Please select Year");
    const suppliersCsv = selectedSuppliers.length > 0 ? `${selectedSuppliers.join(",")},` : "";
    const q = buildPrintQuery({
      branchId: filters.branch,
      month: filters.month,
      year: filters.year,
      itemcategoryid: filters.itemCategoryId || itemCategory.id,
      itemname: filters.itemName || "Select",
      supplier: suppliersCsv || "%",
      date: "",
    });
    navigate(`/vehicle/LubeStockPositionReport_Print?${q}`);
  };

  return (
    <VehicleLayoutWrapper>
      <div className={`w-full max-w-full min-w-0 overflow-x-hidden box-border min-h-screen p-4 md:p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-2xl md:text-3xl">LUBE STOCK POSITION REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={`w-full max-w-full min-w-0 overflow-hidden p-4 md:p-6 mt-6 rounded-2xl shadow-xl border box-border ${
          isDark ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white" : "bg-white text-black"
        }`}>
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div className="min-w-0">
              <h2 className={`text-xl md:text-2xl font-bold break-words ${isDark ? "text-white" : "text-gray-900"}`}>Lube Stock Position Report</h2>
            </div>
            <div className="flex items-center gap-2 md:gap-3 flex-wrap">
              <button onClick={handlePrint} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${buttonClass} ${hoverClass} text-white`}><FaPrint /> Print</button>
              <button onClick={() => toast.info("Excel export not connected")} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"}`}><FaFileExcel /> Excel</button>
              <button onClick={fetchInit} disabled={isBusy} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}><FaSyncAlt /> Refresh</button>
              <button onClick={() => navigate(-1)} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"}`}><FaArrowLeft /> Back</button>
              <button onClick={() => setShowFilters((s) => !s)} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"}`}>
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {showFilters && (
            <div className={`p-4 md:p-6 rounded-lg mb-6 overflow-hidden ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>Search Criteria</h3>
                <div className="flex gap-2 flex-wrap">
                  <button onClick={handleClearFilters} type="button" className={`px-4 py-2 rounded-lg text-sm font-semibold border ${isDark ? "border-gray-600 text-gray-200 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-white"}`}>Clear</button>
                  <button onClick={handleViewAll} disabled={isBusy} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${isDark ? "border-gray-600 text-gray-200 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-white"} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}><FaEye /> View All</button>
                  <button onClick={handleSearch} disabled={isBusy} type="button" className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg ${buttonClass} ${hoverClass} text-white ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}><FaSearch /> {loadingList ? "Searching..." : "Search"}</button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div className="min-w-0">
                  <label className={labelClass}>Branch</label>
                  <select name="branch" value={filters.branch} onChange={handleChange} className={fieldClass} disabled={locks.branchLocked || isBusy}>
                    {(branches || []).map((b) => <option key={toStr(b.value)} value={toStr(b.value)}>{toStr(b.label)}</option>)}
                  </select>
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Month</label>
                  <select name="month" value={filters.month} onChange={handleChange} className={fieldClass} disabled={isBusy}>
                    {(months || []).map((m) => <option key={toStr(m.value)} value={toStr(m.value)}>{toStr(m.label)}</option>)}
                  </select>
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Year</label>
                  <select name="year" value={filters.year} onChange={handleChange} className={fieldClass} disabled={isBusy}>
                    {(years || []).map((y) => <option key={toStr(y.value)} value={toStr(y.value)}>{toStr(y.label)}</option>)}
                  </select>
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Item Category</label>
                  <input className={fieldClass} value={itemCategory.name || "FUELS AND LUBRICANTS"} disabled />
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Item Name</label>
                  <select name="itemName" value={filters.itemName} onChange={handleChange} className={fieldClass} disabled={isBusy}>
                    {(itemNames || []).map((it) => <option key={toStr(it.value)} value={toStr(it.value)}>{toStr(it.label)}</option>)}
                  </select>
                </div>
                <div className="min-w-0 flex items-end">
                  <div className={`text-sm pb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>Suppliers selected: <b>{selectedSuppliers.length}</b></div>
                </div>
              </div>

              <div className="mt-4">
                <label className={labelClass}>Suppliers</label>
                <div className={`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 p-3 rounded-lg border max-h-40 overflow-auto ${isDark ? "border-gray-700 bg-gray-900/20" : "border-gray-200 bg-white"}`}>
                  {loadingInit ? (
                    <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>Loading suppliers...</div>
                  ) : suppliers.length === 0 ? (
                    <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>No suppliers</div>
                  ) : (
                    suppliers.map((s) => {
                      const name = toStr(s.label);
                      return (
                        <label key={toStr(s.value)} className={`flex items-center gap-2 text-sm min-w-0 ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                          <input
                            type="checkbox"
                            checked={selectedSuppliers.includes(name)}
                            onChange={(e) => {
                              setSelectedSuppliers((prev) => (e.target.checked ? [...prev, name] : prev.filter((x) => x !== name)));
                            }}
                          />
                          <span className="truncate">{name}</span>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"}`}>Total Records: {rows.length}</div>
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>{loadingInit ? "Loading dropdowns..." : loadingList ? "Searching..." : "Ready"}</div>
          </div>

          <div className="w-full max-w-full overflow-x-auto rounded-lg border shadow-lg">
            {loadingInit || loadingList ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}><div className={`text-lg font-medium ${isDark ? "text-gray-300" : "text-gray-600"}`}>Loading...</div></div>
            ) : rows.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}>No Records Found</div>
                <p className={`mt-2 text-sm ${isDark ? "text-gray-500" : "text-gray-400"}`}>Select Branch, Month and Year then click Search.</p>
              </div>
            ) : (
              <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                <thead className={`${tableHeaderClass} text-white`}>
                  <tr>
                    <th className="px-3 py-3 text-left text-sm font-semibold whitespace-nowrap">S.No</th>
                    {displayColumns.map((c) => <th key={c} className="px-3 py-3 text-left text-sm font-semibold whitespace-nowrap">{c}</th>)}
                    <th className="px-3 py-3 text-left text-sm font-semibold whitespace-nowrap">Print</th>
                  </tr>
                </thead>
                <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                  {pagedRows.map((r, idx) => (
                    <tr key={idx} className={`${idx % 2 === 0 ? (isDark ? "bg-gray-800" : "bg-white") : (isDark ? "bg-gray-750" : "bg-gray-50")} ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}>
                      <td className={`px-3 py-3 text-sm whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-900"}`}>{(currentPage - 1) * itemsPerPage + idx + 1}</td>
                      {displayColumns.map((c) => (
                        <td key={c} className={`px-3 py-3 text-sm whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-900"}`}>{toStr(r[c], "")}</td>
                      ))}
                      <td className="px-3 py-3">
                        <button onClick={handlePrint} type="button" className={`p-2 rounded ${isDark ? "bg-gray-700 text-white hover:bg-gray-600" : "bg-gray-100 text-gray-800 hover:bg-gray-200"}`} title="Print">
                          <FaPrint className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {rows.length > 0 && (
            <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-3">
              <div className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, rows.length)} of {rows.length} entries
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} type="button" className={`px-4 py-2 border rounded-lg font-semibold ${isDark ? "border-gray-600 text-gray-300 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-gray-100"} ${currentPage === 1 ? "opacity-50 cursor-not-allowed" : ""}`}>Prev</button>
                <div className={`px-4 py-2 border rounded-lg ${isDark ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"}`}>Page {currentPage} / {totalPages}</div>
                <button onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages} type="button" className={`px-4 py-2 border rounded-lg font-semibold ${isDark ? "border-gray-600 text-gray-300 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-gray-100"} ${currentPage === totalPages ? "opacity-50 cursor-not-allowed" : ""}`}>Next</button>
              </div>
            </div>
          )}

          <p className={`text-xs text-right mt-4 ${isDark ? "text-gray-400" : "text-gray-500"}`}>INFRA SCREEN : B-63</p>
          <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} closeOnClick pauseOnHover theme={isDark ? "dark" : "light"} />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default LubeStockPositionReport;